import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import styles from './ChromeStates.module.css';

/**
 * MV5 Sprint 2.D — Global chrome state variants (Figma page 31).
 *
 * Shipping the connectivity + focus-mode bits. Session-expiry banner shares
 * the QuizStates SessionExpiredModal for now — it's the same modal in a
 * different context. ⌘K palette + toast stack deferred (⌘K to Sprint 2.5;
 * Toast Provider already exists in @hauraki/ui).
 */

/**
 * OfflineBanner — top-of-page banner that appears when the browser reports
 * navigator.onLine === false. Auto-managed via window events.
 */
export const OfflineBanner: React.FC = () => {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  if (online) return null;
  return (
    <div className={styles.offlineBanner} role="status" aria-live="polite">
      <WifiOff className={styles.offlineIcon} aria-hidden />
      <span>
        You&rsquo;re offline. Your work is saved locally; syncing resumes when
        you reconnect.
      </span>
    </div>
  );
};

/**
 * MinimizedNavFocusMode — hides the top nav + sidebar when `enabled`. This
 * component just toggles a `body.hauraki-focus` class; consumer stylesheets
 * (Layout.module.css) key off that.
 */
export const MinimizedNavFocusMode: React.FC<{ enabled: boolean }> = ({
  enabled,
}) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('hauraki-focus', enabled);
    return () => document.body.classList.remove('hauraki-focus');
  }, [enabled]);
  return null;
};
